import { supabase } from './supabase';

// Dr C training (Gary, 17 Oct): the drc-practice edge function. Head office and the named reviewers
// review every customer message and play customers in a practice chat with the real Dr C. Nothing here
// reaches WhatsApp.

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/drc-practice`;

export type ItemType = 'template' | 'answer' | 'line';
export type Verdict = 'approve' | 'reject' | 'edit' | 'tick' | 'cross' | 'note';

export interface ReviewItem { type: ItemType; ref: string; group: string; title: string; text: string; note?: string }
export interface MyVerdict { target_type: ItemType; target_ref: string; verdict: Verdict; suggestion: string | null; comment: string | null }
export interface PracticeLine { role: 'customer' | 'drc'; text: string }
export interface PracticeReply { text: string; handed_over: boolean; reason: string | null; summary: string | null; blocked: string | null; tools: string[] }
export interface ResultRow {
  target_type: string; target_ref: string; target_text: string; verdict: Verdict; suggestion: string | null; comment: string | null;
  context: Record<string, unknown>; created_at: string; reviewer: string;
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('You are signed out. Please sign in again.');
  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({})) as T & { error?: string };
  if (!res.ok) throw new Error(json.error ?? 'Something went wrong. Try again.');
  return json;
}

export const loadItems = () => call<{ round: number; head_office: boolean; items: ReviewItem[]; mine: MyVerdict[] }>({ action: 'items' });

export const practiceSay = (messages: PracticeLine[], afterHours: boolean, customer: boolean, name: string | null, handedOver: string | null = null) =>
  call<PracticeReply>({ action: 'say', messages, after_hours: afterHours, customer, name, handed_over: handedOver });

export const saveVerdict = (v: {
  target_type: ItemType | 'practice' | 'comment'; target_ref: string; target_text: string; verdict: Verdict;
  suggestion?: string; comment?: string; context?: Record<string, unknown>;
}) => call<{ saved: boolean }>({ action: 'verdict', ...v });

export const loadResults = () => call<{ round: number; verdicts: ResultRow[] }>({ action: 'results' });

/** Round 1's suggested customer roles (from the 64 simulated chats). Play them in your own words. */
export const ROLES: { label: string; hint: string; afterHours?: boolean; customer?: boolean }[] = [
  { label: 'Just a price', hint: 'Ask what a double bed costs. Only one bed.' },
  { label: 'Big job', hint: 'Four beds, six pillows and a couch, and you want Saturday.' },
  { label: 'No suburb', hint: 'Ask questions but dodge the suburb question once.' },
  { label: 'After hours', hint: 'Write at night and ask to book tomorrow.', afterHours: true },
  { label: 'Annoyed', hint: 'Your clean was late. Repeat yourself if the answer is weak.', customer: true },
  { label: 'Afrikaans', hint: 'Write only in Afrikaans.' },
  { label: 'Haggler', hint: 'Get a quote, then ask for 20% off.' },
  { label: 'Allergies', hint: 'Your child has asthma. Will this help?' },
  { label: 'Stains and bed bugs', hint: 'Ask if we remove stains, then about bed bugs.' },
  { label: 'Returning customer', hint: 'You had a clean before. Book again.', customer: true },
];

/** Is this login a Dr C reviewer? Asked once per visit; a refusal means no. */
let who: Promise<{ reviewer: boolean; head_office: boolean; test_number: string | null }> | null = null;
export function whoAmI() {
  who ??= call<{ reviewer: boolean; head_office: boolean; test_number: string | null }>({ action: 'whoami' })
    .catch(() => ({ reviewer: false, head_office: false, test_number: null }));
  return who;
}

const RATED = 'chats-drc-rated';
/** Thumbs already given on this device, so they stay shown after a reload. */
export function readRated(): Record<string, 'tick' | 'cross'> {
  try { return JSON.parse(localStorage.getItem(RATED) ?? '{}') as Record<string, 'tick' | 'cross'>; } catch { return {}; }
}
export function writeRated(id: string, v: 'tick' | 'cross') {
  try { localStorage.setItem(RATED, JSON.stringify({ ...readRated(), [id]: v })); } catch { /* storage blocked: shown for this visit only */ }
}

export interface BenchTurn { id: string; persona: string; turn: number; role: 'customer' | 'drc' | 'note'; text: string; meta: Record<string, unknown>; created_at: string }
/** The six points of the readiness scorecard, in the card's order (Gary, 18 Oct). */
export const SCORE_POINTS = [
  { key: 'understanding', label: 'Understanding', hint: "Did Leo understand the customer's actual intention?" },
  { key: 'conversation', label: 'Natural conversation', hint: 'Did it sound appropriate rather than scripted?' },
  { key: 'context', label: 'Context', hint: 'Did it remember earlier details and corrections?' },
  { key: 'accuracy', label: 'Accuracy', hint: 'Were prices, claims and availability grounded in approved information?' },
  { key: 'boundaries', label: 'Boundaries', hint: 'Did it respect privacy, money and booking authority?' },
  { key: 'handover', label: 'Hand over', hint: 'Did it recognise when a person was needed?' },
] as const;
export type ScorePoint = typeof SCORE_POINTS[number]['key'];
export type Scores = Record<ScorePoint, number>;
export interface Scorecard extends Scores { persona: string; scorer: string; mine: boolean; critical: boolean; critical_reason: string | null; notes: string | null }
export interface BenchRun { run_id: string; started_at: string; round: number | null }

export const scoreTotal = (c: Scores) => SCORE_POINTS.reduce((t, p) => t + c[p.key], 0);

export const loadBench = (runId?: string) => call<{ run_id: string | null; turns: BenchTurn[]; rated: Record<string, 'tick' | 'cross'>; scorecards?: Scorecard[]; runs?: BenchRun[] }>({ action: 'bench', ...(runId ? { run_id: runId } : {}) });

export const saveScore = (b: Scores & { run_id: string; persona: string; critical: boolean; critical_reason?: string; notes?: string }) =>
  call<{ saved: boolean }>({ action: 'score', ...b });

async function benchCall<T>(body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('You are signed out. Please sign in again.');
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/drc-bench`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({})) as T & { error?: string };
  if (!res.ok) throw new Error(json.error ?? 'The stress test did not start. Try again.');
  return json;
}

/**
 * Starts a stress test run (head office): AI customers chat with the real Dr C. One request per customer, a
 * few seconds apart, so each chat has its own call chain (Supabase throttles one shared chain).
 */
export async function startBench(round?: 1 | 2): Promise<{ run_id: string; personas: string[] }> {
  // A round is the readiness test (Gary, 18 Oct): its 15 customers, scored by the AI judge at the end.
  const set = round ? { set: 'final', round } : {};
  const { personas } = await benchCall<{ personas: string[] }>({ action: 'personas', ...set });
  const runId = crypto.randomUUID();
  await benchCall({ action: 'start', personas: [personas[0]], run_id: runId, ...set });
  personas.slice(1).forEach((p, i) => setTimeout(() => void benchCall({ action: 'start', personas: [p], run_id: runId, ...set }).catch(() => null), (i + 1) * 3000));
  return { run_id: runId, personas };
}
